/* Keeps `route()` on the language of the current URL.

   Ziggy fills a string argument into the first parameter. Once `locale` is that
   first parameter, `route('portfolios.show', slug)` would put the slug in the
   prefix. This reads the prefix off the address and puts it back on every
   public route, and it leaves routes that have no locale parameter alone
   (`blog.show`, admin, the form posts). English omits `locale`, which is what
   leaves `/web-design` unprefixed.

   A caller that passes `ms` or `zh` itself is left alone, so the language
   switcher can point at a different language than the one on screen. An empty
   `locale` is removed: English is the missing parameter. */

const PREFIXES = ['ms', 'zh'];

/* The blade template declares `const Ziggy`, which is a global binding and not
   a property of `window`. Reading `window.Ziggy` from this module misses it,
   the wrapper then hands Ziggy a bare slug, and Ziggy drops that slug into
   `locale`, which only accepts `ms` or `zh`. */
function ziggyState() {
  if (typeof Ziggy !== 'undefined') {
    return Ziggy;
  }

  if (typeof globalThis !== 'undefined' && globalThis.Ziggy) {
    return globalThis.Ziggy;
  }

  return null;
}

function parameterNames(uri) {
  const names = [];
  const pattern = /\{([^}?]+)\??\}/g;
  let match = pattern.exec(uri);

  while (match) {
    names.push(match[1]);
    match = pattern.exec(uri);
  }

  return names;
}

function currentPrefix(ziggyRoute) {
  const current = ziggyRoute();
  const value = current && current.params ? current.params.locale : null;

  return PREFIXES.indexOf(value) === -1 ? null : value;
}

function acceptsLocale(name) {
  const state = ziggyState();
  const definition = state && state.routes && state.routes[name];

  return !!(definition && typeof definition.uri === 'string' && definition.uri.indexOf('{locale') !== -1);
}

/* English is the absence of `locale`. An empty string is still a value, and
   the parameter's constraint is only `ms` or `zh`, so the key is left off. */
function withPrefix(params, prefix) {
  if (!prefix) {
    return params;
  }

  return Object.assign({ locale: prefix }, params);
}

function withoutEmptyLocale(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return params;
  }

  if (!Object.prototype.hasOwnProperty.call(params, 'locale')) {
    return params;
  }

  if (params.locale === '' || params.locale == null) {
    const next = Object.assign({}, params);
    delete next.locale;

    return next;
  }

  return params;
}

function withLocale(name, params, ziggyRoute) {
  if (!acceptsLocale(name)) {
    return params;
  }

  const prefix = currentPrefix(ziggyRoute);

  if (params == null) {
    return prefix ? { locale: prefix } : {};
  }

  if (typeof params === 'string' || typeof params === 'number') {
    const definition = ziggyState().routes[name];
    const names = parameterNames(definition.uri).filter((item) => item !== 'locale');
    const next = {};

    if (names[0]) {
      next[names[0]] = params;
    }

    return withPrefix(next, prefix);
  }

  if (typeof params === 'object' && !Array.isArray(params)) {
    if (Object.prototype.hasOwnProperty.call(params, 'locale')) {
      return withoutEmptyLocale(params);
    }

    return withPrefix(params, prefix);
  }

  return params;
}

export function ziggyRoutes() {
  const state = ziggyState();

  return state && state.routes ? state.routes : null;
}

export function installLocalizedRoute() {
  const ziggyRoute = route;

  window.route = function localizedRoute(name, params, absolute, config) {
    return ziggyRoute(name, withLocale(name, params, ziggyRoute), absolute, config);
  };
}
